<template>
  <div>
    <Dialog :stateId="dialogId" maxWidth="720px" @dialog-closing="close" @close="close">
      <v-card v-if="show" id="tract-visit-detail">
        <v-card-title class="primary white--text">
          <span>{{ visit?.tractVisitTypeName }}</span>
          <v-spacer/>
          <Icon
          v-if="visit"
          icon="mdi-pencil"
          iconColor="white"
          :small="false"
          tabindex="0"
          elementId="tract-visit-detail-edit"
          :tooltipText="$t('edit')"
          @icon-clicked="requestEdit"/>
          <BaseDialogActions
          hideRefresh
          hideFullscreen
          customCancel
          elementId="tract-visit-detail-actions"
          @close="requestClose"/>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row v-if="loading" class="my-12" justify="center" align="center">
            <v-progress-circular indeterminate color="primary"/>
          </v-row>

          <template v-else>
            <v-row dense>
              <v-col cols="12">
                <DetailCard :title="$t('visitDetails')">
                  <v-row no-gutters v-for="row in detailRows" :key="row.key">
                    <v-col cols="6" sm="3">{{ $t(row.key) }}:</v-col>
                    <v-col>
                      <span :style="valueStyle(row.value)">{{ row.value || $t('notSpecified') }}</span>
                    </v-col>
                  </v-row>
                </DetailCard>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12">
                <DetailCard :title="$t('notes')">
                  <span v-if="visit.notes" style="white-space: pre-line;">{{ visit.notes }}</span>
                  <span v-else :style="valueStyle()">{{ $t('notSpecified') }}</span>
                </DetailCard>
              </v-col>
            </v-row>

            <TractVisitAttachments
            class="mt-4"
            :tract="tract"
            :files="files"
            :loading="loadingFiles"
            @file-chosen="uploadFile"/>
          </template>
        </v-card-text>
      </v-card>
    </Dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { tractVisitFilesClient } from '@/utils/TractVisitFiles'
import { helpers } from '@/plugins/theme.js'

export default {
  name: 'TractVisitDetailDialog',

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    TractVisitAttachments: () => import('@/components/tract/tract-visits/TractVisitAttachments.vue')
  },

  props: {
    tract: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    dialogId: uniqueDialogId('tract-visit-detail-dialog'),
    show: false,
    visit: null,
    files: [],
    loading: false,
    loadingFiles: false,
    openRequestId: 0,
    editRequested: null
  }),

  computed: {
    detailRows () {
      const visit = this.visit
      return [
        { key: 'visitType', value: visit.tractVisitTypeName },
        { key: 'completedAt', value: utcToLocalDate(visit.completedAt, 'L - LT') },
        { key: 'createdBy', value: visit.createdByUserName }
      ]
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    valueStyle (value) {
      return { color: value ? undefined : helpers.getTranslucent(25) }
    },

    async open (visit) {
      const requestId = ++this.openRequestId
      this.visit = visit
      this.files = []
      this.show = true
      this.loading = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '720px' })
      try {
        await this.loadFiles()
      } finally {
        if (requestId === this.openRequestId) this.loading = false
      }
    },

    async loadFiles () {
      const requestId = this.openRequestId
      const visitId = this.visit.tractVisitId
      this.loadingFiles = true
      try {
        const files = await tractVisitFilesClient.getFiles(visitId)
        if (requestId === this.openRequestId && this.show) this.files = files
      } finally {
        if (requestId === this.openRequestId) this.loadingFiles = false
      }
    },

    async uploadFile (file) {
      this.loadingFiles = true
      await tractVisitFilesClient.uploadFile(this.visit, file)
      await this.loadFiles()
    },

    requestEdit () {
      this.editRequested = this.visit
      this.requestClose()
    },

    requestClose () {
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    close () {
      const editVisit = this.editRequested
      this.editRequested = null
      this.show = false
      this.visit = null
      this.files = []
      if (editVisit) this.$emit('edit', editVisit)
    }
  }
}
</script>
